import React, { useState, useEffect, useRef } from "react";
import { validateImageSafety, analyzeGroupArrangement } from "./gemini";
import { processImage } from "./ImageUtils";
import { SPLoader } from "./Spinner.js";
import { Loader2 } from "./Icons";

const StyleBuilderModal = ({
  apikey,
  isOpen,
  onClose,
  onFinalize,
  isGroupMode,
  textOnlyDirective,
  onTextOnlyDirectiveChange,
  snippets,
  onSnippetsChange,
  globalCompositionSnippet,
  onGlobalCompositionSnippetChange,
  availableIdentifiers,
  mobile,
  target = "male",
  onTargetChange,
}) => {
  const [finalizingTarget, setFinalizingTarget] = useState(null);
  const isFinalizing = !!finalizingTarget;
  const [isAnalyzingGroup, setIsAnalyzingGroup] = useState(false);
  const [isProcessingSnippets, setIsProcessingSnippets] = useState(false);
  const [isTextDirectiveOpen, setIsTextDirectiveOpen] = useState(false);
  const [finalizeMessage, setFinalizeMessage] = useState("");
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const textDirectiveRef = useRef(null);
  const textDirectiveContainerRef = useRef(null);
  const globalInstructionRef = useRef(null);

  const MAX_SNIPPETS = 10;
  const MAX_CHAR_LIMIT = 200;
  const MAX_GLOBAL_CHAR_LIMIT = 1250;
  const MAX_TEXT_DIRECTIVE_LIMIT = 500;

  useEffect(() => {
    if (globalInstructionRef.current) {
      globalInstructionRef.current.style.height = "auto";
      const newHeight = Math.min(globalInstructionRef.current.scrollHeight, 500);
      globalInstructionRef.current.style.height = `${newHeight}px`;
    }
    // }, [globalCompositionSnippet]);
  }, [globalCompositionSnippet.instruction]);

  const [selectedFigure, setSelectedFigure] = useState("1");
  const [figureIdentifier, setFigureIdentifier] = useState("");
  const [mappings, setMappings] = useState({});
  const [figureInstructions, setFigureInstructions] = useState({});
  const [blueprintDirective, setBlueprintDirective] = useState("");
  const [editingFigure, setEditingFigure] = useState(null);
  const [editingText, setEditingText] = useState("");

  const applyAllMappings = (text, currentMappings) => {
    let result = text;
    // Sort by figure number descending to avoid partial replacements (e.g. Figure 10 vs Figure 1)
    const sortedFigs = Object.keys(currentMappings).sort((a, b) => parseInt(b) - parseInt(a));

    sortedFigs.forEach((figNum) => {
      const id = currentMappings[figNum];
      if (id && id !== "REMOVE") {
        const regex = new RegExp(`Figure ${figNum}`, "gi");
        result = result.replace(regex, id);
      }
    });
    return result;
  };

  const revertAllMappings = (text, currentMappings) => {
    let result = text;
    // Sort mappings by identifier length descending to avoid partial replacements
    const sortedMappings = Object.entries(currentMappings)
      .filter(([_, id]) => id && id !== "REMOVE")
      .sort((a, b) => b[1].length - a[1].length);

    sortedMappings.forEach(([figNum, id]) => {
      const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(escapedId, "gi");
      result = result.replace(regex, `Figure ${figNum}`);
    });
    return result;
  };

  const getFigureBlocks = (instruction) => {
    if (!instruction) return [];
    const mainInstruction = instruction.split("CRITICAL REMOVAL LIST:")[0].trim();
    // Match each block: "X. Map **[ANYTHING]:** ... until the next number or end"
    const blockRegex = /\d+\.\s*Map\s+\*\*[\s\S]*?:\*\*[\s\S]*?(?=(?:\n\d+\.\s*Map\s+\*\*)|$)/gi;
    const blocks = mainInstruction.match(blockRegex) || [];
    return blocks.map((block) => block.trim());
  };

  // Initialize figure instructions and mappings from the snippet
  useEffect(() => {
    if (globalCompositionSnippet) {
      const instruction =
        globalCompositionSnippet.originalInstruction || globalCompositionSnippet.instruction;
      const blocks = getFigureBlocks(instruction);
      const newFigInst = {};

      blocks.forEach((block, idx) => {
        const figMatch = block.match(/Figure\s+(\d+)/i);
        const figNum = figMatch ? figMatch[1] : (idx + 1).toString();
        newFigInst[figNum] = block;
      });

      setFigureInstructions(newFigInst);
      const sortedFigs = Object.keys(newFigInst).sort((a, b) => parseInt(a) - parseInt(b));
      if (sortedFigs.length > 0 && !newFigInst[selectedFigure]) {
        setSelectedFigure(sortedFigs[0]);
      }
      // We don't reset mappings here if we want to preserve them across small changes,
      // but usually a new image means new figures.
      setMappings({});
    }
  }, [globalCompositionSnippet.image.preview]);

  // Sync the final instruction whenever mappings or figureInstructions change
  useEffect(() => {
    if (!globalCompositionSnippet) return;

    // Build the instruction from figureInstructions and mappings
    let newInstructionBlocks = [];
    const figNums = Object.keys(figureInstructions).sort((a, b) => parseInt(a) - parseInt(b));

    figNums.forEach((figNum) => {
      const id = mappings[figNum];
      if (id === "REMOVE") return; // Skip removed figures in the final instruction

      let block = figureInstructions[figNum];
      // if (id) {
      //   const figureTag = `Figure ${figNum}`;
      //   const regex = new RegExp(figureTag, "gi");
      //   block = block.replace(regex, id);
      // }
      block = applyAllMappings(block, mappings);
      newInstructionBlocks.push(block);
    });

    // MANDATORY IDENTITY REPLACEMENT DIRECTIVE:
    // This ensures no original models from the reference image leak into the final output.
    let newInstruction =
      `MANDATORY IDENTITY REPLACEMENT: DO NOT RENDER ANY FIGURES FROM THE REFERENCE IMAGE. EVERY SINGLE FIGURE IN THE FINAL OUTPUT MUST BE REPLACED BY ONE OF THE PROVIDED IDENTIFIERS. NO ORIGINAL MODELS OR FIGURES SHOULD BE VISIBLE IN THE FINAL OUTPUT.\n\n` +
      newInstructionBlocks.join("\n\n");

    // Add custom blueprint directive if present
    if (blueprintDirective.trim()) {
      newInstruction += `\n\nCUSTOM DIRECTIVES: ${blueprintDirective.trim()}`;
    }

    // Clean up extra whitespace and multiple periods
    newInstruction = newInstruction.replace(/\s+/g, " ").replace(/\.+/g, ".").trim();

    // Add explicit removal list if any figures are removed
    const removedFigures = Object.entries(mappings)
      .filter(([_, id]) => id === "REMOVE")
      .map(([fig, _]) => `Figure ${fig}`);

    if (removedFigures.length > 0) {
      newInstruction += `\n\nCRITICAL REMOVAL LIST: The following figures from the reference image MUST be completely removed and NOT rendered: ${removedFigures.join(
        ", "
      )}. The final output MUST contain exactly ${availableIdentifiers.length} subjects.`;
    }

    // FINAL ZERO LEAKAGE REQUIREMENT
    newInstruction += `\n\nFINAL REQUIREMENT: ZERO LEAKAGE. Under no circumstances should any visual information (faces, bodies, clothing) from the reference image's original models be present in the final output. Every person or animal must be rendered strictly based on the provided identifiers.`;

    // Only update if the instruction has actually changed to avoid infinite loops
    if (newInstruction !== globalCompositionSnippet.instruction) {
      onGlobalCompositionSnippetChange({
        ...globalCompositionSnippet,
        instruction: newInstruction,
      });
    }
  }, [mappings, figureInstructions, blueprintDirective, globalCompositionSnippet.id]);

  const applyFigureMapping = () => {
    if (!globalCompositionSnippet || !figureIdentifier.trim()) return;

    const newIdentifier = figureIdentifier.trim();

    setMappings((prev) => {
      const updated = { ...prev };

      // Enforce one-to-one mapping:
      // If the identifier is already mapped to another figure, remove that mapping first
      if (newIdentifier !== "REMOVE") {
        Object.keys(updated).forEach((figNum) => {
          if (updated[figNum] === newIdentifier && figNum !== selectedFigure) {
            delete updated[figNum];
          }
        });
      }

      updated[selectedFigure] = newIdentifier;
      return updated;
    });

    setFigureIdentifier("");
  };

  const resetMappings = () => {
    if (!globalCompositionSnippet) return;
    setMappings({});
    setFigureIdentifier("");
    setBlueprintDirective("");

    // Also reset figureInstructions to original state
    const instruction =
      globalCompositionSnippet.originalInstruction || globalCompositionSnippet.instruction;
    const blocks = getFigureBlocks(instruction);
    const newFigInst = {};
    blocks.forEach((block, idx) => {
      const figMatch = block.match(/Figure\s+(\d+)/i);
      const figNum = figMatch ? figMatch[1] : (idx + 1).toString();
      newFigInst[figNum] = block;
    });
    setFigureInstructions(newFigInst);
  };

  if (!isOpen) return null;

  function showSpinner() {
    document.getElementById("loading-overlay").classList.remove("hidden");
  }

  function hideSpinner() {
    document.getElementById("loading-overlay").classList.add("hidden");
  }

  const handleSnippetUpload = (e, isGlobal) => {
    const files = e.target.files;
    if (!files) return;

    if (isGlobal) {
      const file = files[0];
      const processFile = async () => {
        setIsAnalyzingGroup(true);
        const processed = await processImage(file);
        if (processed) {
          try {
            const arrangement = await analyzeGroupArrangement(apikey, processed);
            onGlobalCompositionSnippetChange({
              id: "global-composition",
              image: processed,
              instruction: arrangement,
              originalInstruction: arrangement,
              baseInstructionLength: arrangement.length,
            });
          } catch (err) {
            console.error("Failed to analyze group arrangement:", err);
            onGlobalCompositionSnippetChange({
              id: "global-composition",
              image: processed,
              instruction: "",
              originalInstruction: "",
            });
          }
        }
        setIsAnalyzingGroup(false);
      };
      processFile();
      e.target.value = "";
      return;
    }

    const currentCount = snippets.length;
    const remainingSlots = MAX_SNIPPETS - currentCount;

    if (remainingSlots <= 0) {
      setError(`Maximum limit of ${MAX_SNIPPETS} snippets reached.`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      setError(
        `Only added the first ${remainingSlots} images to stay within the ${MAX_SNIPPETS} snippet limit.`
      );
    }

    const processFiles = async () => {
      setIsProcessingSnippets(true);
      try {
        const newSnippets = [];
        for (const file of filesToProcess) {
          const processed = await processImage(file);
          if (processed) {
            newSnippets.push({
              id: Math.random().toString(36).substr(2, 9),
              image: processed,
              instruction: "",
            });
          }
        }
        if (newSnippets.length > 0) {
          onSnippetsChange([...snippets, ...newSnippets]);
        }
      } catch (err) {
        console.error("Failed to process snippets:", err);
        setError("Failed to process one or more images.");
      } finally {
        setIsProcessingSnippets(false);
      }
    };

    processFiles();
    e.target.value = "";
  };

  const updateSnippetInstruction = (id, text) => {
    if (text.length > MAX_CHAR_LIMIT) return;
    onSnippetsChange(snippets.map((s) => (s.id === id ? { ...s, instruction: text } : s)));
  };

  const removeSnippet = (id) => {
    onSnippetsChange(snippets.filter((s) => s.id !== id));
    setError(null);
  };

  const handleAddTextDirective = () => {
    if (!isTextDirectiveOpen) {
      setIsTextDirectiveOpen(true);
      setTimeout(() => {
        textDirectiveContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        textDirectiveRef.current.focus();
      }, 100);
    } else {
      textDirectiveContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      textDirectiveRef.current.focus();
    }
  };

  const generateCatalogue = async (target) => {
    if (snippets.length === 0 && !textOnlyDirective) return;
    showSpinner();
    setFinalizingTarget(target);
    setError(null);
    setFinalizeMessage("Synthesizing catalogue components...");

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cols = 2;
    // const hasGlobal = !!globalCompositionSnippet;
    const hasGlobal = !!globalCompositionSnippet.id;
    const rows = Math.ceil(snippets.length / cols) + (hasGlobal ? 1 : 0);
    const cellWidth = 600;
    const cellHeight = 800;
    const padding = 100;
    const headerHeight = 180;

    canvas.width = cellWidth * cols + padding * (cols + 1);
    canvas.height =
      cellHeight * rows + padding * (rows + 1) + headerHeight + (textOnlyDirective ? 140 : 0);

    ctx.fillStyle = "#020617";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#6366f1";
    ctx.font = "bold 50px Inter, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`TECHNICAL REPLICATION BLUEPRINT: ${target.toUpperCase()}`, canvas.width / 2, 70);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "24px Inter, sans-serif";
    ctx.fillText(
      "DIRECTIVE: REPLICATE ALL ATTIRE, JEWELRY, AND COMPOSITION WITH 1:1 FIDELITY.",
      canvas.width / 2,
      115
    );

    const drawSnippet = (snippet, index, isGlobal = false) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          let col, row;
          if (isGlobal) {
            col = 0;
            row = 0;
          } else {
            const adjustedIndex = hasGlobal ? index + 2 : index; // Move snippets down if global exists
            col = adjustedIndex % cols;
            row = Math.floor(adjustedIndex / cols);
          }

          const x = padding + col * (cellWidth + padding);
          const y = headerHeight + padding + row * (cellHeight + padding);

          const actualCellWidth = isGlobal ? cellWidth * 2 + padding : cellWidth;

          ctx.strokeStyle = isGlobal ? "#fbbf24" : "#334155";
          ctx.lineWidth = 6;
          ctx.strokeRect(x, y, actualCellWidth, cellHeight);

          const labelAreaHeight = 160;
          const imgAreaHeight = cellHeight - labelAreaHeight;

          let dw = actualCellWidth - 40;
          let dh = (img.height / img.width) * dw;
          if (dh > imgAreaHeight - 40) {
            dh = imgAreaHeight - 40;
            dw = (img.width / img.height) * dh;
          }

          const dx = x + (actualCellWidth - dw) / 2;
          const dy = y + 20 + (imgAreaHeight - 40 - dh) / 2;

          ctx.drawImage(img, dx, dy, dw, dh);

          ctx.fillStyle = isGlobal ? "#451a03" : "#1e293b";
          ctx.fillRect(x + 2, y + imgAreaHeight, actualCellWidth - 4, labelAreaHeight - 2);

          ctx.fillStyle = "#f8fafc";
          ctx.font = "bold 36px Inter, sans-serif";
          ctx.textAlign = "center";
          const instruction =
            snippet.instruction ||
            (isGlobal ? "REPLICATE GLOBAL COMPOSITION & POSES" : "REPLICATE EXACT ATTIRE");
          ctx.fillText(
            instruction.toUpperCase(),
            x + actualCellWidth / 2,
            y + imgAreaHeight + 80,
            actualCellWidth - 40
          );

          ctx.fillStyle = isGlobal ? "#fbbf24" : "#818cf8";
          ctx.font = "24px Inter, sans-serif";
          ctx.fillText(
            isGlobal ? "MASTER COMPOSITION BLUEPRINT" : "SOURCE BLUEPRINT: SNIPPET " + (index + 1),
            x + actualCellWidth / 2,
            y + imgAreaHeight + 130
          );

          resolve();
        };
        img.src = snippet.image.preview;
      });
    };

    if (hasGlobal && globalCompositionSnippet) {
      await drawSnippet(globalCompositionSnippet, 0, true);
    }

    for (let i = 0; i < snippets.length; i++) {
      await drawSnippet(snippets[i], i);
    }

    if (textOnlyDirective) {
      const directiveY = headerHeight + cellHeight * rows + padding * (rows + 1);
      ctx.fillStyle = "#1e1b4b";
      ctx.fillRect(padding, directiveY, canvas.width - padding * 2, 100);
      ctx.strokeStyle = "#4f46e5";
      ctx.lineWidth = 2;
      ctx.strokeRect(padding, directiveY, canvas.width - padding * 2, 100);

      ctx.fillStyle = "#e0e7ff";
      ctx.font = "bold 24px Inter, sans-serif";
      ctx.textAlign = "left";

      // Handle multi-line if needed, but for now simple wrap check
      ctx.fillText(
        "GLOBAL DIRECTIVE: " + textOnlyDirective.toUpperCase(),
        padding + 30,
        directiveY + 58,
        canvas.width - padding * 2 - 60
      );
    }

    const dataUrl = canvas.toDataURL("image/png");
    const base64 = dataUrl.split(",")[1];
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], `style-builder-${target}.png`, {
      type: "image/png",
    });

    const finalImage = { file, preview: dataUrl, base64 };

    // MANDATORY SAFETY VALIDATION BEFORE FINALIZING
    setFinalizeMessage("Performing Security Scan...");
    try {
      const safety = await validateImageSafety(apikey, finalImage);
      hideSpinner();
      if (!safety.safe) {
        setError(safety.reason || "The composed catalogue contains prohibited content.");
        setFinalizingTarget(null);
        setFinalizeMessage("");
        alert(safety.reason || "The composed catalogue contains prohibited content.");
        hideSpinner();
        return;
      }

      onFinalize(finalImage, target);
      setFinalizingTarget(null);
      onClose();
    } catch (err) {
      console.error(err);
      setError("An error occurred during security validation.");
      setFinalizingTarget(null);
      setFinalizeMessage("");
      alert("An error occurred during security validation.");
      hideSpinner();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center md:p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300"
      style={{ top: "13%" }}
    >
      <div className="glass w-full h-full md:max-w-6xl md:max-h-[92vh] md:rounded-[3rem] overflow-hidden flex flex-col border-indigo-500/30 shadow-[0_0_50px_rgba(79,70,229,0.2)]">
        {/* Header */}
        <div className="px-6 py-4 md:px-10 md:py-8 border-b border-white/5 flex justify-between items-center bg-slate-900/60">
          <div className="flex items-center space-x-3">
            {/* <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20"> */}
            <div
              className="w-8 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20"
              style={{ height: "40px", width: "40px" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // className="h-5 w-5 md:h-6 md:w-6 text-white"
                className="w-5 md:h-6 md:w-6 text-white"
                style={{ height: "30px", width: "24px" }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-white tracking-tight">
                Style Builder
              </h2>
              <p className="text-[8px] md:text-sm text-indigo-400/80 font-bold uppercase tracking-[0.2em]">
                Replication Engine
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 md:p-3 hover:bg-white/10 rounded-full transition-all group active:scale-90"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              // className="h-6 w-6 md:h-8 md:w-8 text-slate-400 group-hover:text-white"
              className="w-6 md:h-8 md:w-8 text-slate-400 group-hover:text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar bg-slate-950/20">
          {/* <div className="max-w-5xl mx-auto space-y-8 md:space-y-10"> */}
          <div className="max-w-5xl mx-auto space-y-8 md:space-y-10" style={{ margin: "3%" }}>
            {/* Gender-specific style profile switcher */}
            {!isGroupMode && target && onTargetChange && (
              <div className="flex justify-center">
                <div className="inline-flex p-10 bg-slate-900/80 border border-indigo-500/15 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                  <button
                    onClick={() => onTargetChange("male")}
                    className={`px-6 py-2 rounded-xl text-xs font-black tracking-widest uppercase transition-all duration-300 ${
                      target === "male"
                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/10"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    MALE STYLE BLUEPRINT
                  </button>
                  <button
                    onClick={() => onTargetChange("female")}
                    className={`px-6 py-2 rounded-xl text-xs font-black tracking-widest uppercase transition-all duration-300 ${
                      target === "female"
                        ? "bg-rose-600 text-white shadow-lg shadow-rose-600/10"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    FEMALE STYLE BLUEPRINT
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 text-sm font-bold flex items-center space-x-3 animate-shake">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10"> */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-10">
              {[
                {
                  title: "REPLICATION",
                  desc: `Upload up to ${MAX_SNIPPETS} snippets of attire to be cloned.`,
                },
                {
                  title: "BLUEPRINT",
                  desc: "Gemini will treat these as a technical master guide.",
                },
                {
                  title: "GLOBAL REF",
                  desc: "Use a master image for group composition & poses.",
                },
                {
                  title: "ETHEREAL",
                  desc: "Strictly professional studio standards applied.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  // className="bg-indigo-500/5 border border-indigo-500/10 p-4 rounded-2xl"
                  // className="bg-indigo-500/5 border border-indigo-500/10 rounded-2xl"
                  className="bg-indigo-500/5 border border-indigo-500/10 p-3 md:p-4 rounded-xl md:rounded-2xl"
                  // style={{ padding: "16px", width: "95%", height: "100%", marginBottom: "10px" }}
                >
                  {/* <span className="text-[10px] font-black text-indigo-400 tracking-[0.3em] block mb-1"> */}
                  <span className="text-[10px] font-black text-indigo-400 tracking-[0.2em] block mb-1">
                    {item.title}
                  </span>
                  <p className="text-xs text-slate-400 font-medium">{item.desc}</p>
                </div>
              ))}
            </div>

            {isGroupMode && (
              <div className="space-y-6">
                {/* {!globalCompositionSnippet ? ( */}
                {!globalCompositionSnippet.id ? (
                  // <label className="w-full h-40 flex flex-col items-center justify-center border-2 border-dashed border-amber-500/30 rounded-[2.5rem] text-amber-500 hover:border-amber-500/50 hover:bg-amber-500/5 transition-all cursor-pointer group">
                  <label className="w-full h-150 flex flex-col items-center justify-center border-2 border-dashed bg-amber-500/50 rounded-[2rem] text-amber-500 hover:border-amber-500/50 hover:bg-amber-500/5 transition-all cursor-pointer group">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        // className="h-6 w-6"
                        className="w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h4
                      className={`text-sm font-black uppercase text-amber-400 ${
                        mobile ? "" : "tracking-widest"
                      } flex items-center gap-2`}
                    >
                      {/* {isAnalyzingGroup && <Loader2 className="w-4 h-4" />} */}
                      {isAnalyzingGroup && <Loader2 className="w-4 mr-2" />}
                      {isAnalyzingGroup
                        ? "Analyzing Group Arrangement..."
                        : "Add Global Composition Reference"}
                    </h4>
                    <p className="text-[10px] text-amber-600/70 mt-1 font-bold uppercase tracking-wider flex items-center gap-1.5">
                      {/* {isAnalyzingGroup && <Loader2 className="w-3 h-3 opacity-70" />} */}
                      {isAnalyzingGroup && <Loader2 className="w-3 opacity-70 mr-4" />}
                      {isAnalyzingGroup
                        ? "Gemini is identifying figures and positions..."
                        : "Master guide for group poses & arrangement"}
                    </p>
                    <input
                      type="file"
                      accept="image/*,.heic,.heif,.avif"
                      className="hidden"
                      onChange={(e) => handleSnippetUpload(e, true)}
                      disabled={isAnalyzingGroup}
                    />
                  </label>
                ) : (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-[2rem] overflow-hidden animate-in zoom-in-95 duration-300">
                    <div className="flex justify-center items-center flex-col md:flex-row">
                      {/* <div className="md:w-1/3 aspect-[4/3] md:aspect-auto relative bg-black/20"> */}
                      <div
                        className="md:aspect-auto relative bg-black/20"
                        style={{ aspectRatio: "4/3", width: "33.3333%" }}
                      >
                        <img
                          src={globalCompositionSnippet.image.preview}
                          alt="Global Ref"
                          // className="w-full h-full object-contain"
                          className="w-full h-auto object-cover overflow-y-auto"
                        />
                        <button
                          // onClick={() => onGlobalCompositionSnippetChange(null)}
                          onClick={() =>
                            onGlobalCompositionSnippetChange({
                              id: null,
                              image: {
                                file: null,
                                preview: null,
                                base64: null,
                              },
                              instruction: null,
                              baseInstructionLength: null,
                              originalInstruction: null,
                            })
                          }
                          // className="absolute top-4 left-4 w-8 h-8 bg-black/60 backdrop-blur-md text-white rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                          className="absolute top-4 ml-6 bg-black backdrop-blur-md text-white rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                          style={{ height: "20px", width: "20px" }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            // className="h-4 w-4"
                            className="w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                      {/* <div className="flex-1 p-6 md:p-8 space-y-6"> */}
                      <div className="flex-1 md:p-4 space-y-6">
                        <div className="space-y-4">
                          {/* <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 space-y-3"> */}
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl md:p-4 space-y-3">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  // className="h-4 w-4 text-amber-500"
                                  className="w-4 text-amber-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                                <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">
                                  Refine Blueprint Instructions
                                </span>
                              </div>
                              <span
                                className={`text-[9px] font-bold ${
                                  globalCompositionSnippet.instruction.length >=
                                  (globalCompositionSnippet.baseInstructionLength
                                    ? globalCompositionSnippet.baseInstructionLength + 500
                                    : MAX_GLOBAL_CHAR_LIMIT)
                                    ? "text-amber-500"
                                    : "text-slate-500"
                                }`}
                              >
                                {globalCompositionSnippet.instruction.length}/
                                {globalCompositionSnippet.baseInstructionLength
                                  ? globalCompositionSnippet.baseInstructionLength + 500
                                  : MAX_GLOBAL_CHAR_LIMIT}
                              </span>
                            </div>

                            <div className="space-y-3">
                              {/* <div className="bg-amber-500/50 border border-amber-500/20 rounded-lg p-2">
                                <p
                                  className="text-[9px] text-amber-400 font-bold uppercase tracking-widest leading-normal"
                                  style={{ padding: "1%" }}
                                >
                                  <span className="text-amber-500">Pro Tip:</span> Replace{" "}
                                  <span className="text-white">"Figure X"</span> with your subject
                                  labels (e.g., <span className="text-white">"BOY"</span>,{" "}
                                  <span className="text-white">"MANAGER"</span>) to ensure perfect
                                  identity mapping.
                                </p>
                              </div> */}

                              {/* Figure Mapping Tool */}
                              {/* <div className="bg-slate-950/60 border border-amber-500/20 rounded-xl p-3 flex flex-wrap items-center gap-3"> */}
                              <div className="bg-slate-950/60 border border-amber-500/20 rounded-xl p-12 flex flex-wrap items-center gap-3">
                                {/* <div className="flex flex-col gap-1"> */}
                                <div className="flex-1 flex flex-col ">
                                  {/* <label className="text-[8px] font-black text-amber-500/70 uppercase tracking-widest"> */}
                                  <label className="text-[9px] font-black text-amber-500 uppercase tracking-widest">
                                    Select Figure
                                  </label>
                                  <select
                                    value={selectedFigure}
                                    onChange={(e) => setSelectedFigure(e.target.value)}
                                    // className="bg-slate-900 border border-amber-500/20 rounded-lg px-3 py-1 text-[10px] text-amber-100 outline-none focus:border-amber-500 transition-all"
                                    className="bg-slate-900 border border-amber-500/20 rounded-lg px-3 text-[10px] text-amber-100 outline-none focus:border-amber-500 transition-all"
                                    style={{ height: "30px" }}
                                  >
                                    {Object.keys(figureInstructions)
                                      .sort((a, b) => parseInt(a) - parseInt(b))
                                      .map((num) => {
                                        const mappedTo = mappings[num];
                                        return (
                                          <option key={num} value={num}>
                                            Figure {num}
                                            {mappedTo ? ` => '${mappedTo}'` : ""}
                                          </option>
                                        );
                                      })}
                                  </select>
                                </div>
                                {/* <div className="flex-1 flex flex-col gap-1"> */}
                                <div className="flex-1 flex flex-col ">
                                  {/* <label className="text-[8px] font-black text-amber-500/70 uppercase tracking-widest"> */}
                                  <label className="text-[9px] font-black text-amber-500 uppercase tracking-widest">
                                    Identifier
                                  </label>
                                  <select
                                    value={figureIdentifier}
                                    onChange={(e) => setFigureIdentifier(e.target.value)}
                                    // className="bg-slate-900 border border-amber-500/20 rounded-lg px-3 py-1 text-[10px] text-amber-100 outline-none focus:border-amber-500 transition-all w-full"
                                    className="bg-slate-900 border border-amber-500/20 rounded-lg px-3 text-[10px] text-amber-100 outline-none focus:border-amber-500 transition-all w-full"
                                    style={{ height: "30px" }}
                                  >
                                    <option value="">Choose identifier...</option>
                                    <option value="REMOVE" className="text-red-400/80 font-bold">
                                      REMOVE (Delete Figure)
                                    </option>
                                    {availableIdentifiers
                                      .filter((id) => id.trim() !== "")
                                      .map((id, idx) => {
                                        const mappedFigure = Object.entries(mappings).find(
                                          ([_, val]) => val === id
                                        );
                                        // )[0];
                                        return (
                                          <option key={idx} value={id}>
                                            '{id}'{mappedFigure ? ` => Figure ${mappedFigure}` : ""}
                                          </option>
                                        );
                                      })}
                                  </select>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-evenly",
                                }}
                              >
                                <button
                                  onClick={applyFigureMapping}
                                  disabled={!figureIdentifier.trim()}
                                  // className={`mt-4 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                                  //   !figureIdentifier.trim()
                                  //     ? "bg-slate-800 text-slate-600"
                                  //     : "bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-600/20 active:scale-95"
                                  // }`}
                                  className={`px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                                    !figureIdentifier.trim()
                                      ? "bg-slate-800 text-slate-600"
                                      : "bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-600/20 active:scale-95"
                                  }`}
                                  // style={{ marginTop: "18px" }}
                                >
                                  Apply Mapping
                                </button>
                                <button
                                  onClick={resetMappings}
                                  // className="mt-4 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-all border border-slate-700"
                                  className="px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-all border border-slate-700"
                                  // style={{ marginTop: "18px" }}
                                >
                                  Reset All
                                </button>
                              </div>

                              <p className="text-[9px] text-amber-500 italic font-bold uppercase tracking-tighter">
                                Use these identifiers to map your source images in the main studio.
                                You can edit the detected arrangement below.
                              </p>
                            </div>

                            {(() => {
                              const figNums = Object.keys(figureInstructions).sort(
                                (a, b) => parseInt(a) - parseInt(b)
                              );

                              if (figNums.length === 0) {
                                return (
                                  <textarea
                                    ref={globalInstructionRef}
                                    value={globalCompositionSnippet.instruction}
                                    maxLength={
                                      globalCompositionSnippet.baseInstructionLength
                                        ? globalCompositionSnippet.baseInstructionLength + 500
                                        : MAX_GLOBAL_CHAR_LIMIT
                                    }
                                    onChange={(e) =>
                                      onGlobalCompositionSnippetChange({
                                        ...globalCompositionSnippet,
                                        instruction: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. 'Arrange the group according to this Image: Place Member 1 in the center...'"
                                    // className="w-full bg-slate-950/40 border border-amber-500/10 rounded-xl px-4 py-3 text-xs text-amber-100/90 outline-none focus:border-amber-500/30 transition-all resize-none min-h-[12rem] font-medium leading-relaxed overflow-y-auto custom-scrollbar"
                                    className="w-full bg-slate-950/40 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-100 outline-none focus:border-amber-500/30 transition-all resize-none min-h-[12rem] font-medium leading-relaxed overflow-y-auto custom-scrollbar"
                                    style={{
                                      fieldSizing: "content",
                                      maxHeight: mobile ? "70vh" : "",
                                      minHeight: "100px",
                                    }}
                                  />
                                );
                              }

                              const removalListMatch = globalCompositionSnippet.instruction.match(
                                /CRITICAL REMOVAL LIST:[\s\S]*$/
                              );
                              const removalList = removalListMatch ? removalListMatch[0] : null;

                              return (
                                <div className="space-y-4">
                                  <div className="space-y-2">
                                    {figNums.map((figNum) => {
                                      const block = figureInstructions[figNum];
                                      const mappedTo = mappings[figNum];

                                      // Apply ALL mappings to the brief for display
                                      // let displayBrief = applyAllMappings(
                                      //   block.split("(")[0].trim(),
                                      //   mappings
                                      // );
                                      let displayBrief = applyAllMappings(
                                        block.split("(")[0].trim(),
                                        mappings
                                      ).replace(/\*\*/g, "");

                                      if (mappedTo === "REMOVE") {
                                        displayBrief = displayBrief.replace(
                                          new RegExp(`Figure ${figNum}`, "i"),
                                          `Figure ${figNum} (REMOVED)`
                                        );
                                      }

                                      return (
                                        <div
                                          key={figNum}
                                          className={`flex items-center justify-between border rounded-xl p-3 group/brief transition-all ${
                                            mappedTo === "REMOVE"
                                              ? "bg-red-500/5 border-red-500/20 opacity-60"
                                              : "bg-slate-950/40 border-amber-500/20"
                                            // : "bg-slate-950/40 border-amber-500/10"
                                          }`}
                                        >
                                          <span
                                            // className={`text-xs font-medium ${
                                            //   mappedTo === "REMOVE"
                                            //     ? "text-red-400/80"
                                            //     : "text-amber-100/90"
                                            // }`}
                                            className={`text-xs p-4 font-medium ${
                                              mappedTo === "REMOVE"
                                                ? "text-red-400/80"
                                                : "text-amber-100"
                                            }`}
                                          >
                                            {displayBrief}
                                          </span>
                                          <button
                                            onClick={() => {
                                              setEditingFigure(figNum);
                                              // Apply ALL mappings to the detailed text for display
                                              // setEditingText(applyAllMappings(block, mappings));
                                              setEditingText(
                                                applyAllMappings(block, mappings).replace(
                                                  /\*\*/g,
                                                  ""
                                                )
                                              );
                                            }}
                                            className="px-3 py-1.5 bg-amber-600/10 hover:bg-amber-600/30 text-amber-400 text-[10px] font-black uppercase tracking-widest rounded-lg border border-amber-500/20 transition-all shadow-sm"
                                          >
                                            Detailed Mapping
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {removalList && (
                                    <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                                      <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest leading-relaxed">
                                        {removalList}
                                      </p>
                                    </div>
                                  )}

                                  {/* BLUEPRINT Text Directive */}
                                  <div className="space-y-2 pt-2">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" />
                                      <span className="text-[10px] font-black text-amber-500/80 uppercase tracking-[0.2em]">
                                        BLUEPRINT Text Directive
                                      </span>
                                    </div>
                                    <textarea
                                      value={blueprintDirective}
                                      onChange={(e) => setBlueprintDirective(e.target.value)}
                                      placeholder="e.g. 'Replicate this exact pose. Use this dress code...'"
                                      className="w-full bg-slate-950/60 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-100/90 outline-none focus:border-amber-500/40 transition-all resize-none min-h-[6rem] font-medium leading-relaxed custom-scrollbar"
                                      style={{
                                        fieldSizing: "content",
                                        maxHeight: mobile ? "70vh" : "",
                                        minHeight: "100px",
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {snippets.length === 0 ? (
              // <div className="h-80 flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-[2.5rem] text-slate-500 hover:border-indigo-500/30 transition-colors group">
              <div className="md:h-80 p-12 flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-[2rem] text-slate-500 hover:border-indigo-500/30 transition-colors group">
                {/* <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500"> */}
                <div className="w-40 bg-slate-900 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  {isProcessingSnippets ? (
                    <Loader2 className="h-8 w-8 md:h-10 md:w-10 text-indigo-500" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      // className="h-10 w-10 opacity-30"
                      className="opacity-30"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2-2v12a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-300">
                  {isProcessingSnippets ? "Processing Snippets..." : "Assemble Your Blueprint"}
                </h3>
                <p className="text-sm max-w-[300px] text-center mt-2 font-medium italic">
                  {isProcessingSnippets
                    ? "Gemini is optimizing your images for replication..."
                    : "Upload snippets of high-end attire or jewelry for 1:1 replication."}
                </p>
                <label className="mt-8 px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm cursor-pointer shadow-lg shadow-indigo-600/20 active:scale-95 transition-all">
                  {isProcessingSnippets ? "Processing..." : "Upload Blueprint Snippet"}
                  <input
                    type="file"
                    multiple
                    accept="image/*,.heic,.heif,.avif"
                    className="hidden"
                    onChange={handleSnippetUpload}
                    disabled={isProcessingSnippets}
                  />
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-10">
                {snippets.map((snippet) => (
                  <div
                    key={snippet.id}
                    className="bg-slate-900/40 rounded-[2rem] border border-white/5 overflow-hidden group hover:border-indigo-500/40 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] transition-all duration-500"
                  >
                    <div className="aspect-[4/5] relative">
                      <img
                        src={snippet.image.preview}
                        alt="Snippet"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <button
                        onClick={() => removeSnippet(snippet.id)}
                        // className="absolute top-4 right-4 w-10 h-10 bg-red-500/20 backdrop-blur-xl border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100"
                        className={
                          mobile
                            ? "absolute top-4 right-8 ml-6 bg-red-500 backdrop-blur-xl border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all opacity-100 group-hover:opacity-100 scale-90 group-hover:scale-100"
                            : "absolute top-4 right-8 ml-6 md:w-10 md:h-10 bg-red-500 backdrop-blur-xl border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100"
                        }
                        style={mobile ? { height: "30px", width: "30px" } : {}}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          // className="h-5 w-5"
                          className="w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="p-6 space-y-3">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] block">
                          Replication Directive
                        </label>
                        <span
                          className={`text-[9px] font-bold ${
                            snippet.instruction.length >= MAX_CHAR_LIMIT
                              ? "text-amber-500"
                              : "text-slate-500"
                          }`}
                        >
                          {snippet.instruction.length}/{MAX_CHAR_LIMIT}
                        </span>
                      </div>
                      <textarea
                        value={snippet.instruction}
                        maxLength={MAX_CHAR_LIMIT}
                        onChange={(e) => updateSnippetInstruction(snippet.id, e.target.value)}
                        placeholder="e.g. 'Replicate this exact emerald necklace' or 'Use this specific silk pattern'"
                        className="w-full bg-slate-950/60 border border-white/10 rounded-2xl px-4 py-3 text-sm text-indigo-100 outline-none focus:border-indigo-500/50 transition-all resize-none h-28 font-medium"
                        style={{
                          fieldSizing: "content",
                          maxHeight: mobile ? "70vh" : "",
                          minHeight: "100px",
                        }}
                      />
                    </div>
                  </div>
                ))}

                {snippets.length < MAX_SNIPPETS && (
                  // <label className="aspect-[4/5] flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-[2rem] text-slate-600 hover:border-indigo-500/50 hover:bg-indigo-500/5 cursor-pointer transition-all duration-300 group">
                  <label
                    className={`aspect-[4/5] flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-[2rem] text-slate-600 hover:border-indigo-500/50 hover:bg-indigo-500/5 cursor-pointer transition-all duration-300 group ${
                      isProcessingSnippets ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                      {isProcessingSnippets ? (
                        <Loader2 className="h-8 w-8 text-white" />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          // className="h-8 w-8"
                          className="md:h-8 w-8"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-black uppercase tracking-[0.15em]">
                      {isProcessingSnippets ? "Processing..." : "Add Blueprint"}
                    </span>
                    <span className="text-[9px] text-slate-600 mt-1 uppercase font-bold">
                      {snippets.length} / {MAX_SNIPPETS} slots used
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.heic,.heif,.avif"
                      className="hidden"
                      onChange={handleSnippetUpload}
                      disabled={isProcessingSnippets}
                    />
                  </label>
                )}
              </div>
            )}

            {isTextDirectiveOpen && (
              <div
                ref={textDirectiveContainerRef}
                className="bg-indigo-500/10 border border-indigo-500/30 rounded-[2rem] p-8 animate-in zoom-in-95 duration-300 relative group/text"
              >
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-3">
                    {/* <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"> */}
                    <div className="w-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        // className="h-5 w-5 text-white"
                        className="w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white">Global Text Directive</h3>
                  </div>
                  <button
                    onClick={() => {
                      setIsTextDirectiveOpen(false);
                      onTextOnlyDirectiveChange("");
                    }}
                    className="absolute top-6 right-6 w-10 h-10 bg-red-500/20 backdrop-blur-xl border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover/text:opacity-100 scale-90 group-hover/text:scale-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-indigo-300/70 font-medium italic">
                    This directive applies to the entire generation alongside your visual snippets.
                  </p>
                  <span
                    className={`text-[10px] font-bold ${
                      textOnlyDirective.length >= MAX_TEXT_DIRECTIVE_LIMIT
                        ? "text-amber-500"
                        : "text-slate-500"
                    }`}
                  >
                    {textOnlyDirective.length}/{MAX_TEXT_DIRECTIVE_LIMIT}
                  </span>
                </div>
                <textarea
                  ref={textDirectiveRef}
                  value={textOnlyDirective}
                  maxLength={MAX_TEXT_DIRECTIVE_LIMIT}
                  onChange={(e) => onTextOnlyDirectiveChange(e.target.value)}
                  placeholder="e.g. 'Ensure all clothing has a matte finish' or 'Apply a high-contrast cinematic lighting style'"
                  className="w-full bg-slate-950/60 border border-indigo-500/30 rounded-2xl px-6 py-4 text-indigo-100 outline-none focus:border-indigo-500 transition-all resize-none h-32 font-medium"
                  style={{
                    fieldSizing: "content",
                    maxHeight: mobile ? "70vh" : "",
                    minHeight: "100px",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-6 md:px-10 md:py-8 border-t border-white/5 bg-slate-900/80 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 w-full md:w-auto">
            <div className="flex flex-col items-center md:items-start">
              <span className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Blueprint Status
              </span>
              <span className="text-sm md:text-xl font-bold text-white uppercase">
                {isFinalizing ? finalizeMessage : `${snippets.length} / ${MAX_SNIPPETS} COMPONENTS`}
              </span>
            </div>

            <button
              onClick={handleAddTextDirective}
              className={`w-full md:w-auto px-4 py-3 md:py-2 rounded-xl text-[10px] font-black tracking-widest transition-all border ${
                isTextDirectiveOpen
                  ? "bg-indigo-600 border-indigo-500 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600"
              }`}
            >
              {textOnlyDirective ? "EDIT TEXT DIRECTIVE" : "ADD TEXT ONLY DIRECTIVE"}
            </button>
          </div>

          <div className="flex items-center space-x-3 md:space-x-4 w-full md:w-auto">
            {!isGroupMode ? (
              target === "male" ? (
                <React.Fragment>
                  <button
                    disabled={(snippets.length === 0 && !textOnlyDirective) || isFinalizing}
                    onClick={() => generateCatalogue("male")}
                    className={`flex-1 md:flex-none px-4 md:px-6 py-4 rounded-2xl text-[10px] font-black tracking-[0.15em] transition-all shadow-2xl ${
                      (snippets.length === 0 && !textOnlyDirective) || isFinalizing
                        ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-500 text-white active:scale-95"
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {finalizingTarget === "male" && (
                        <svg
                          // className="animate-spin h-3 w-3 text-white"
                          className="animate-spin w-4 mr-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      <span>
                        {finalizingTarget === "male" ? "SCANNING..." : "FINALIZE FOR MALE"}
                      </span>
                    </div>
                  </button>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <button
                    disabled={(snippets.length === 0 && !textOnlyDirective) || isFinalizing}
                    onClick={() => generateCatalogue("female")}
                    className={`flex-1 md:flex-none px-4 md:px-6 py-4 rounded-2xl text-[10px] font-black tracking-[0.15em] transition-all shadow-2xl ${
                      (snippets.length === 0 && !textOnlyDirective) || isFinalizing
                        ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                        : "bg-rose-600 hover:bg-rose-500 text-white active:scale-95"
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {finalizingTarget === "female" && (
                        <svg
                          // className="animate-spin h-3 w-3 text-white"
                          className="animate-spin w-4 mr-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      <span>
                        {finalizingTarget === "female" ? "SCANNING..." : "FINALIZE FOR FEMALE"}
                      </span>
                    </div>
                  </button>
                </React.Fragment>
              )
            ) : (
              <React.Fragment>
                <button
                  disabled={(snippets.length === 0 && !textOnlyDirective) || isFinalizing}
                  onClick={() => generateCatalogue("group")}
                  className={`w-full md:w-auto px-6 py-4 rounded-2xl text-[10px] font-black tracking-[0.15em] transition-all shadow-2xl ${
                    (snippets.length === 0 && !textOnlyDirective) || isFinalizing
                      ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                      : "bg-slate-600 hover:bg-slate-500 text-white active:scale-95"
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {finalizingTarget === "group" && (
                      <svg
                        // className="animate-spin h-3 w-3 text-white"
                        className="animate-spin w-4 mr-3 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                    <span>{finalizingTarget === "group" ? "SCANNING..." : "FINALIZE GROUP"}</span>
                  </div>
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Mapping Edit Modal */}
      {editingFigure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          {/* <div className="bg-slate-900 border border-amber-500/30 rounded-[2.5rem] w-full max-w-4xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300"> */}
          <div className="bg-slate-900 p-12 border border-amber-500/20 rounded-[2rem] w-full max-w-4xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-10 h-10 bg-amber-600/20 rounded-xl flex items-center justify-center"> */}
                  <div
                    className="bg-amber-600/20 rounded-xl flex items-center justify-center"
                    style={{ height: "40px", width: "40px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      // className="h-6 w-6 text-amber-500"
                      className="text-amber-500"
                      style={{ height: "24px", width: "24px" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Edit Figure {editingFigure} Mapping
                  </h3>
                </div>
                <button
                  onClick={() => setEditingFigure(null)}
                  // className="w-10 h-10 bg-slate-800 text-slate-400 rounded-full flex items-center justify-center hover:bg-slate-700 hover:text-white transition-all"
                  className="bg-slate-800 text-slate-400 rounded-full flex items-center justify-center hover:bg-slate-700 hover:text-white transition-all"
                  style={{ height: "40px", width: "40px" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    // className="h-6 w-6"
                    className="w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                {globalCompositionSnippet && (
                  // <div className="md:w-1/2 aspect-square md:aspect-auto bg-black/40 rounded-2xl overflow-hidden border border-amber-500/20">
                  <div
                    className="flex justify-center items-center md:aspect-auto  bg-black/20 rounded-2xl overflow-hidden border border-amber-500/20 overflow-y-auto"
                    style={{ aspectRatio: "1/2", width: "33.3333%" }}
                  >
                    {/* style={{ aspectRatio: "4/3", width: "33.3333%" }} */}
                    <img
                      src={globalCompositionSnippet.image.preview}
                      alt="Global Ref"
                      // className="w-full h-full object-contain"
                      className="w-full h-auto object-cover "
                    />
                  </div>
                )}
                <div className="flex-1 space-y-4">
                  <p className="text-xs text-slate-400 font-medium italic">
                    Edit the detailed anatomical description for this figure.{" "}
                    <span className="text-amber-400/80 font-bold">Note:</span> Please do not edit
                    the subject identifier (e.g. "
                    {mappings[editingFigure] || `Figure ${editingFigure}`}") directly; use the
                    mapping dropdown in the main view to change assignments.
                  </p>
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="w-full h-full bg-slate-950 border border-amber-500/20 rounded-2xl px-6 py-4 text-sm text-amber-100 outline-none focus:border-amber-500 transition-all resize-none h-64 md:h-full font-medium leading-relaxed custom-scrollbar"
                    style={{
                      fieldSizing: "content",
                      maxHeight: mobile ? "70vh" : "",
                      minHeight: "100px",
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4">
                <button
                  onClick={() => setEditingFigure(null)}
                  className="px-6 py-3 bg-slate-800 text-slate-400 rounded-xl font-bold text-sm hover:bg-slate-700 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!editingFigure) return;
                    // Revert ALL mappings back to Figure X tags before saving
                    const textToSave = revertAllMappings(editingText, mappings);

                    setFigureInstructions((prev) => ({
                      ...prev,
                      [editingFigure]: textToSave,
                    }));
                    setEditingFigure(null);
                  }}
                  className="px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-amber-600/20 active:scale-95 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default StyleBuilderModal;
